import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import ExtractionSetupStepper from "./ExtractionSetupStepper";
import DeviceCodePrompt from "./DeviceCodePrompt";
import ServerControl from "./ServerControl";
import HomeHeader from "./HomeHeader";
import HomeNav from "./HomeNav";
import HomeContent from "./HomeContent";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { db } from "./firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  deleteDoc,
} from "firebase/firestore";
import {
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Paper,
  Dialog,
  Modal,
  IconButton,
  Popover,
} from "@mui/material";
import axios from "axios";
import "./App.css";

const allowedUsers = ["o2fGLZzblHZknP6Vw1o4MwfDGyW2"];

function Home() {
  const {
    currentUser,
    logout,
    officeUser,
    officeLogin,
    accessToken,
    refreshAccessToken,
    username,
    usernameLoading,
  } = useAuth();
  
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const selectedFolderId = ""; 
  const destinationFolderId = "";
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [stepperDialogOpen, setStepperDialogOpen] = useState(false);
  const [deviceCodeDialogOpen, setDeviceCodeDialogOpen] = useState(false);
  const [deviceCodeData, setDeviceCodeData] = useState(null);
  const [serverStatus, setServerStatus] = useState("loading");
  const [modalOpen, setModalOpen] = useState(false);
  const [activeButton, setActiveButton] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [subscriptionKey, setSubscriptionKey] = useState("");
  const setLogsVisible = useState(false);

  useEffect(() => {
    const fetchBaseUrl = async () => {
      const serverDocRef = doc(db, "config", "server");
      try {
        const docSnap = await getDoc(serverDocRef);
        if (docSnap.exists()) {
          setBaseUrl(docSnap.data().baseUrl);
        } else {
          console.error("No such document!");
        }
      } catch (error) {
        console.error("Error fetching base URL:", error);
      }
    };

    const fetchSubscriptionKey = async () => {
      const apiConfigDocRef = doc(db, "config", "keys");
      try {
        const docSnap = await getDoc(apiConfigDocRef);
        if (docSnap.exists()) {
          setSubscriptionKey(docSnap.data().subscriptionKey);
        } else {
          console.error("No API subscription key document found!");
        }
      } catch (error) {
        console.error("Error fetching API subscription key:", error);
      }
    };

    fetchBaseUrl();
    fetchSubscriptionKey();
  }, []);

  useEffect(() => {
    const checkAccessToken = async () => {
      try {
        await refreshAccessToken();
        console.log("Access token refreshed");
      } catch (error) {
        console.error("Failed to refresh access token:", error);
        officeLogin();
      }
    };

    const interval = setInterval(checkAccessToken, 60000);
    return () => clearInterval(interval);
  }, [officeUser, refreshAccessToken, officeLogin]);

  useEffect(() => {
    const deviceCodeRef = doc(db, "deviceCodes", "current");
    const unsubscribe = onSnapshot(deviceCodeRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        setDeviceCodeData(docSnapshot.data());
      } else {
        setDeviceCodeData(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchServerStatus = useCallback(async () => {
    if (!baseUrl || !subscriptionKey) return;
    try {
      const response = await axios.get(`${baseUrl}/status`, {
        headers: {
          "Ocp-Apim-Subscription-Key": subscriptionKey,
        },
      });
      setServerStatus(response.data.status);
    } catch (error) {
      console.error("Error fetching server status:", error);
      setServerStatus("error");
    }
  }, [baseUrl, subscriptionKey]);
  
  useEffect(() => {
    fetchServerStatus();
  }, [fetchServerStatus]);

  const handleChipClick = () => {
    if (deviceCodeData) {
      handleOpenDeviceCodeDialog();
    } else {
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleCloseDeviceCodeDialog = () => {
    setDeviceCodeDialogOpen(false);
  };

  const fetchCurrentFolders = useCallback(async () => {
    if (officeUser && officeUser.username) {
      const settingsRef = doc(db, "settings", officeUser.username);
      try {
        const docSnap = await getDoc(settingsRef);
        if (docSnap.exists()) {
          setSelectedFolders(docSnap.data().selectedMailboxFolders || []);
        } else {
          console.log("No document found at the specified path.");
        }
      } catch (error) {
        console.error("Failed to fetch current selected folders:", error);
      }
    } else {
      console.log("Office user or username is undefined, unable to fetch folders.");
    }
  }, [officeUser]);

  useEffect(() => {
    if (officeUser) {
      fetchCurrentFolders();
    }
  }, [officeUser, fetchCurrentFolders]);

  const handleComponentSelection = (component, button) => {
    setSelectedComponent(component);
    setActiveButton(button);
  };

  const handleLogsVisibilityChange = (visible) => {
    setLogsVisible(visible);
  };

  const handleOpenLogsDialog = () => {
    if (allowedUsers.includes(currentUser.uid)) {
      setLogsDialogOpen(true);
    } else {
      alert("Access Denied: You do not have permission to view Logs.");
    }
  };

  const handleCloseLogsDialog = () => {
    setLogsDialogOpen(false);
  };

  const handleOpenStepperDialog = () => {
    if (allowedUsers.includes(currentUser.uid)) {
      setStepperDialogOpen(true);
    } else {
      alert("Access Denied: You do not have permission to perform this action.");
    }
  };

  const handleCloseStepperDialog = () => {
    setStepperDialogOpen(false);
  };

  const handleOpenDeviceCodeDialog = () => {
    setDeviceCodeDialogOpen(true);
  };


  const handleOpenConfirm = (event) => {
    if (allowedUsers.includes(currentUser.uid)) {
      setAnchorEl(event.currentTarget);
      setConfirmOpen(true);
    } else {
      alert("Access Denied: You do not have permission to remove the account.");
    }
  };

  const handleCloseConfirm = () => {
    setAnchorEl(null);
    setConfirmOpen(false);
  };

  const handleDeleteAccount = async () => {
    try {
      const userDocRef = doc(db, "settings", officeUser.username);
      await deleteDoc(userDocRef);
  
      // Also check if this user is currently assigned as the service account
      const serviceRef = doc(db, "serviceUsername", "current");
      const serviceSnap = await getDoc(serviceRef);
  
      if (serviceSnap.exists()) {
        const currentServiceUser = serviceSnap.data().username;
        if (currentServiceUser === officeUser.username) {
          await deleteDoc(serviceRef);
          console.log("Deleted service account assignment.");
        }
      }
  
      alert("Account deleted successfully.");
      logout();
      handleCloseConfirm();
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("Failed to delete the account. Please try again.");
    }
  };

  if (usernameLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading account details...</Typography>
        </Box>
      </Box>
    );
  }
  

  return (
    <Box sx={{ flexGrow: 1 }}>
      <HomeHeader
        logout={logout}
        serverStatus={serverStatus}
        deviceCodeData={deviceCodeData}
        onChipClick={handleChipClick}
      />
      <Toolbar>
        <Typography variant="h4" gutterBottom>
          Welcome, {currentUser.email}
        </Typography>
      </Toolbar>
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="server-control-modal-title"
        aria-describedby="server-control-modal-description"
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Paper sx={{ position: "relative", p: 2, maxWidth: 500, width: "90%" }}>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              overflow: "auto",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography id="server-control-modal-title" variant="h6" component="h2">
            Extractor Service
          </Typography>
          <ServerControl
            baseUrl={baseUrl}
            username={username}
            allowed={allowedUsers.includes(currentUser.uid)}
            onLogsVisibilityChange={handleLogsVisibilityChange}
          />
        </Paper>
      </Modal>
      <Dialog
        open={deviceCodeDialogOpen}
        onClose={handleCloseDeviceCodeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DeviceCodePrompt
          open={deviceCodeDialogOpen}
          onClose={handleCloseDeviceCodeDialog}
          username={username}
        />
      </Dialog>
      <Container>
        <Box sx={{ my: 4 }}>
          <Dialog
            open={stepperDialogOpen}
            onClose={handleCloseStepperDialog}
            fullWidth
            maxWidth="lg"
          >
            <ExtractionSetupStepper
              accessToken={accessToken}
              userId={officeUser && officeUser.username}
              onClose={handleCloseStepperDialog}
            />
          </Dialog>
          {!officeUser && (
            <>
              <Typography variant="h6" gutterBottom>
                {username
                  ? `Log In to Microsoft as ${username}`
                  : "Log In to Microsoft"}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={officeLogin}
                className="button"
              >
                Login
              </Button>
            </>
          )}
          {officeUser && officeUser.username && (
            <Box
              my={4}
              p={2}
              component={Paper}
              elevation={3}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6" gutterBottom>
                Extractor Mail Account: {username}
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleOpenConfirm}
                className="button"
              >
                Remove Account
              </Button>
              <Popover
                open={confirmOpen}
                anchorEl={anchorEl}
                onClose={handleCloseConfirm}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
              >
                <Box p={2}>
                  <Typography>
                    Are you sure you want to remove your mail account {username}
                    ?
                  </Typography>
                  <Box mt={2} display="flex" justifyContent="space-between">
                    <Button
                      variant="contained"
                      onClick={handleCloseConfirm}
                      className="button"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleDeleteAccount}
                      className="button"
                    >
                      Yes, Remove Account
                    </Button>
                  </Box>
                </Box>
              </Popover>
            </Box>
          )}

          {officeUser && selectedFolders.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                From Mailfolders:
              </Typography>
              <ul>
                {selectedFolders.map((folder) => (
                  <li key={folder.id}>
                    {folder.mailboxName}\{folder.displayName}
                  </li>
                ))}
              </ul>
            </>
          )}
          {officeUser && (
            <>
              <HomeNav
                activeButton={activeButton}
                handleOpenStepperDialog={handleOpenStepperDialog}
                handleOpenLogsDialog={handleOpenLogsDialog}
                handleComponentSelection={handleComponentSelection}
                allowedUsers={allowedUsers}
                currentUser={currentUser}
              />
              <HomeContent
                selectedComponent={selectedComponent}
                accessToken={accessToken}
                officeUser={officeUser}
                selectedFolders={selectedFolders}
                baseUrl={baseUrl}
                username={username}
                allowed={allowedUsers.includes(currentUser.uid)}
                handleLogsVisibilityChange={handleLogsVisibilityChange}
                logsDialogOpen={logsDialogOpen}
                handleCloseLogsDialog={handleCloseLogsDialog}
                selectedFolderId={selectedFolderId}
                destinationFolderId={destinationFolderId}
              />
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default Home;
